<template>
  <div class="knowledge-base-page">
    <header class="kb-header">
      <div class="kb-header-inner">
        <h1 class="kb-title">{{ $t('KnowledgeBasePage.title') }}</h1>
        <div class="kb-search-wrap">
          <el-input
            v-model="searchKey"
            clearable
            class="kb-search-input"
            :placeholder="$t('KnowledgeBasePage.searchPlaceholder')"
            @keyup.enter.native="fetchList(true)"
            @clear="fetchList(true)"
          >
            <el-button
              slot="append"
              type="primary"
              icon="el-icon-search"
              class="kb-search-btn"
              @click="fetchList(true)"
            />
          </el-input>
        </div>
        <el-button type="text" class="kb-back" @click="goHome">
          <i class="el-icon-back" />
          {{ $t('KnowledgeBasePage.backHome') }}
        </el-button>
      </div>
    </header>

    <div class="kb-body">
      <aside class="kb-sidebar">
        <div class="kb-sidebar-head">
          <span class="kb-sidebar-head-title">{{ $t('KnowledgeBasePage.filterTitle') }}</span>
          <button type="button" class="kb-link-reset" @click="resetAllFilters">
            {{ $t('KnowledgeBasePage.reset') }}
          </button>
        </div>

        <div v-if="tagsLoading" class="kb-tags-loading">
          <i class="el-icon-loading" />
        </div>
        <el-collapse v-else v-model="activeCollapseNames" class="kb-filter-collapse">
          <el-collapse-item
            :title="$t('KnowledgeBasePage.timeRange')"
            :name="timePanelName"
          >
            <div class="kb-year-fields">
              <div class="kb-year-row">
                <span class="kb-year-label">{{ $t('KnowledgeBasePage.startYear') }}</span>
                <el-date-picker
                  v-model="startYear"
                  type="year"
                  value-format="yyyy"
                  format="yyyy"
                  :placeholder="$t('KnowledgeBasePage.yearPlaceholder')"
                  size="small"
                  class="kb-year-picker"
                  @change="onYearChange"
                />
              </div>
              <div class="kb-year-row">
                <span class="kb-year-label">{{ $t('KnowledgeBasePage.endYear') }}</span>
                <el-date-picker
                  v-model="endYear"
                  type="year"
                  value-format="yyyy"
                  format="yyyy"
                  :placeholder="$t('KnowledgeBasePage.yearPlaceholder')"
                  size="small"
                  class="kb-year-picker"
                  @change="onYearChange"
                />
              </div>
            </div>
          </el-collapse-item>
          <el-collapse-item
            v-for="cat in tagCategories"
            :key="cat.tag_sort"
            :title="cat.tag_sort"
            :name="cat.tag_sort"
          >
            <el-input
              v-model="tagQuery[cat.tag_sort]"
              clearable
              size="small"
              :placeholder="$t('KnowledgeBasePage.searchInFilter')"
              class="kb-inline-search"
              @input="onTagQueryInput(cat.tag_sort)"
            />
            <el-checkbox-group
              v-model="selectedTagIdsBySort[cat.tag_sort]"
              class="kb-checkbox-group"
              @change="onTagSelectionChange"
            >
              <el-checkbox
                v-for="tag in visibleTags(cat)"
                :key="tag.id"
                :label="tag.id"
                class="kb-checkbox"
              >
                <span class="kb-checkbox-label">{{ tag.name }}</span>
                <span v-if="tag.count != null" class="kb-tag-count">({{ tag.count }})</span>
              </el-checkbox>
            </el-checkbox-group>
            <button
              v-if="showExpandMore(cat)"
              type="button"
              class="kb-expand-link"
              @click="toggleTagExpand(cat.tag_sort)"
            >
              {{ $t('KnowledgeBasePage.expandMore') }}
            </button>
            <button
              v-if="showCollapseLess(cat)"
              type="button"
              class="kb-expand-link"
              @click="toggleTagExpand(cat.tag_sort)"
            >
              {{ $t('KnowledgeBasePage.collapseLess') }}
            </button>
          </el-collapse-item>
        </el-collapse>

        <div v-if="activeFilterChips.length" class="kb-selected-panel">
          <div class="kb-selected-head">
            <span>{{ $t('KnowledgeBasePage.selectedTitle') }}</span>
            <button type="button" class="kb-link-clear" @click="clearSelectionOnly">
              {{ $t('KnowledgeBasePage.clearSelection') }}
            </button>
          </div>
          <div class="kb-chip-list">
            <span
              v-for="(c, idx) in activeFilterChips"
              :key="chipKey(c, idx)"
              class="kb-chip"
            >
              {{ c.label }}
              <i class="el-icon-close kb-chip-x" @click="removeFilterChip(c)" />
            </span>
          </div>
        </div>
      </aside>

      <main class="kb-main">
        <div v-loading="listLoading" class="kb-main-inner">
          <div class="kb-toolbar">
            <span class="kb-result-count">{{ $t('KnowledgeBasePage.resultCount', { n: total }) }}</span>
            <el-radio-group v-model="viewMode" class="kb-view-toggle" size="small">
              <el-radio-button label="card">{{ $t('KnowledgeBasePage.cardView') }}</el-radio-button>
              <el-radio-button label="list">{{ $t('KnowledgeBasePage.listView') }}</el-radio-button>
            </el-radio-group>
          </div>

          <div v-if="!listLoading && items.length === 0" class="kb-empty">
            {{ $t('KnowledgeBasePage.empty') }}
          </div>

          <div v-show="!listLoading && items.length && viewMode === 'card'" class="kb-card-grid">
            <article
              v-for="item in items"
              :key="item.id"
              class="kb-card"
            >
              <div class="kb-card-head">
                <img
                  class="kb-card-icon"
                  :src="getFileIcon(item.title)"
                  alt=""
                >
                <div class="kb-card-head-main">
                  <h2 class="kb-card-title" :title="item.title">{{ item.title }}</h2>
                  <div v-if="item.tags && item.tags.length" class="kb-card-tags">
                    <span
                      v-for="(t, ti) in item.tags"
                      :key="ti"
                      class="kb-tag-pill"
                    >{{ t.tag_name }}</span>
                  </div>
                  <div class="kb-card-meta-line">
                    {{ formatYear(item.publish_year) }}
                  </div>
                </div>
              </div>
              <p class="kb-card-summary">{{ item.summary || $t('KnowledgeBasePage.noSummary') }}</p>
              <div class="kb-card-footer">
                <el-button
                  type="text"
                  class="kb-download-link"
                  :disabled="!isPdfFile(item.title)"
                  @click="handlePreview(item)"
                >
                  <i class="el-icon-view" />
                  {{ $t('KnowledgeBasePage.preview') }}
                </el-button>
                <el-button
                  type="text"
                  class="kb-download-link"
                  disabled
                >
                  <i class="el-icon-download" />
                  {{ $t('KnowledgeBasePage.download') }}
                </el-button>
              </div>
            </article>
          </div>

          <div v-show="!listLoading && items.length && viewMode === 'list'" class="kb-list">
            <div
              v-for="item in items"
              :key="'l-' + item.id"
              class="kb-list-row"
            >
              <img class="kb-list-icon" :src="getFileIcon(item.title)" alt="">
              <div class="kb-list-main">
                <div class="kb-list-title-row">
                  <span class="kb-list-title">{{ item.title }}</span>
                  <div v-if="item.tags && item.tags.length" class="kb-list-tags">
                    <span
                      v-for="(t, ti) in item.tags"
                      :key="ti"
                      class="kb-tag-pill kb-tag-pill--sm"
                    >{{ t.tag_name }}</span>
                  </div>
                </div>
                <div class="kb-list-meta">
                  {{ formatYear(item.publish_year) }}
                </div>
                <p class="kb-list-summary">{{ item.summary || $t('KnowledgeBasePage.noSummary') }}</p>
              </div>
              <div class="kb-list-actions">
                <el-button
                  type="text"
                  class="kb-download-link"
                  :disabled="!isPdfFile(item.title)"
                  @click="handlePreview(item)"
                >
                  {{ $t('KnowledgeBasePage.preview') }}
                </el-button>
                <el-button
                  type="text"
                  class="kb-download-link"
                  disabled
                >
                  {{ $t('KnowledgeBasePage.download') }}
                </el-button>
              </div>
            </div>
          </div>

          <div v-if="total > 0" class="kb-pagination-wrap">
            <el-pagination
              :current-page="pageNo"
              :page-sizes="[10, 20, 50]"
              :page-size="pageSize"
              layout="total, sizes, prev, pager, next, jumper"
              :total="total"
              @size-change="onPageSizeChange"
              @current-change="onPageChange"
            />
          </div>
        </div>
      </main>
    </div>

    <el-dialog
      :title="previewTitle"
      :visible.sync="previewVisible"
      width="80%"
      top="5vh"
      class="kb-preview-dialog"
      append-to-body
      @close="closePreviewDialog"
    >
      <div v-loading="previewLoading" class="kb-preview-body">
        <iframe
          v-if="previewUrl"
          :src="previewUrl"
          class="kb-preview-iframe"
          title="PDF preview"
        />
      </div>
    </el-dialog>
  </div>
</template>

<script>
import {
  getKnowledgeBaseCategoryAndTag,
  getKnowledgeBaseList,
  getMinioFileUrl,
  getKnowledgeFilePreview,
} from '@/api/index';

const MAX_TAGS_COLLAPSED = 6;

export default {
  name: 'KnowledgeBase',
  data() {
    return {
      timePanelName: '__kb_time__', // 时间面板名称
      searchKey: '', // 搜索关键词
      startYear: '', // 起始年份（yyyy）
      endYear: '', // 结束年份（yyyy）
      tagCategories: [], // 标签分类
      selectedTagIdsBySort: {}, // 已选标签ID
      activeCollapseNames: [], // 展开的标签分类
      tagQuery: {}, // 标签查询
      tagExpanded: {}, // 展开的标签分类
      tagsLoading: false, // 标签加载状态
      listLoading: false, // 列表加载状态
      items: [], // 列表数据
      total: 0, // 总条数
      pageNo: 1, // 当前页码
      pageSize: 20, // 每页条数
      searchDebounceTimer: null, // 搜索防抖定时器
      viewMode: 'card', // 视图模式
      maxTagsCollapsed: MAX_TAGS_COLLAPSED, // 最大展开标签数
      previewVisible: false, // 预览框可见状态
      previewLoading: false, // 预览加载状态
      previewUrl: '', // 预览URL
      previewTitle: '', // 预览标题
    };
  },
  computed: {
    // 已选的筛选条件
    activeFilterChips() {
      const chips = [];
      if (this.startYear || this.endYear) {
        const s = this.startYear || '—';
        const e = this.endYear || '—';
        chips.push({
          type: 'time',
          label: this.$t('KnowledgeBasePage.timeChip', { s, e }),
        });
      }
      this.tagCategories.forEach((cat) => {
        const ids = this.selectedTagIdsBySort[cat.tag_sort] || [];
        ids.forEach((id) => {
          const tag = (cat.tag_list || []).find((t) => String(t.id) === String(id));
          if (tag) {
            chips.push({
              type: 'tag',
              sort: cat.tag_sort,
              id,
              label: `${cat.tag_sort}: ${tag.name}`,
            });
          }
        });
      });
      return chips;
    },
  },
  mounted() {
    this.loadCategories();
    this.fetchList(true);
  },
  beforeDestroy() {
    if (this.searchDebounceTimer) clearTimeout(this.searchDebounceTimer);
    this.revokePreviewUrl();
  },
  methods: {
    onYearChange() {
      this.fetchList(true);
    },
    // 筛选条件key
    chipKey(c, idx) {
      if (c.type === 'time') return 'chip-time';
      return `chip-${c.sort}-${c.id}-${idx}`;
    },
    // 跳转首页
    goHome() {
      // 返回上一页
      this.$router.back();
    },
    // 初始化已选标签
    initSelectionForCategories(data) {
      const next = {};
      const names = [this.timePanelName];
      (data || []).forEach((cat) => {
        this.$set(next, cat.tag_sort, []);
        names.push(cat.tag_sort);
      });
      this.selectedTagIdsBySort = next;
      this.activeCollapseNames = names;
    },
    // 重置所有筛选条件
    resetAllFilters() {
      this.searchKey = '';
      this.startYear = '';
      this.endYear = '';
      this.tagExpanded = {};
      this.initSelectionForCategories(this.tagCategories);
      const q = {};
      this.tagCategories.forEach((cat) => {
        q[cat.tag_sort] = '';
      });
      this.tagQuery = q;
      this.fetchList(true);
    },
    // 清空所有筛选条件
    clearSelectionOnly() {
      this.startYear = '';
      this.endYear = '';
      this.tagExpanded = {};
      this.initSelectionForCategories(this.tagCategories);
      this.fetchList(true);
    },
    // 移除筛选条件
    removeFilterChip(c) {
      if (c.type === 'time') {
        this.startYear = '';
        this.endYear = '';
        this.fetchList(true);
        return;
      }
      const arr = this.selectedTagIdsBySort[c.sort];
      if (!arr || !arr.length) return;
      const idx = arr.findIndex((x) => String(x) === String(c.id));
      if (idx >= 0) arr.splice(idx, 1);
      this.fetchList(true);
    },
    // 标签查询输入
    onTagQueryInput(sort) {
      this.$set(this.tagExpanded, sort, false);
    },
    // 过滤标签
    getFilteredTags(cat) {
      const q = (this.tagQuery[cat.tag_sort] || '').trim().toLowerCase();
      const list = cat.tag_list || [];
      if (!q) return list;
      return list.filter((t) => (t.name || '').toLowerCase().includes(q));
    },
    // 可见标签
    visibleTags(cat) {
      const filtered = this.getFilteredTags(cat);
      const lim = this.maxTagsCollapsed;
      if (filtered.length <= lim || this.tagExpanded[cat.tag_sort]) {
        return filtered;
      }
      return filtered.slice(0, lim);
    },
    // 显示更多标签
    showExpandMore(cat) {
      return this.getFilteredTags(cat).length > this.maxTagsCollapsed
        && !this.tagExpanded[cat.tag_sort];
    },
    // 收起标签
    showCollapseLess(cat) {
      return this.getFilteredTags(cat).length > this.maxTagsCollapsed
        && this.tagExpanded[cat.tag_sort];
    },
    // 切换标签展开状态
    toggleTagExpand(sort) {
      this.$set(this.tagExpanded, sort, !this.tagExpanded[sort]);
    },
    // 加载标签分类
    async loadCategories() {
      this.tagsLoading = true;
      try {
        const res = await getKnowledgeBaseCategoryAndTag();
        const data = res.data.data.tags || [];
        this.tagCategories = data;
        this.initSelectionForCategories(data);
        const q = {};
        data.forEach((cat) => {
          q[cat.tag_sort] = this.tagQuery[cat.tag_sort] != null ? this.tagQuery[cat.tag_sort] : '';
        });
        this.tagQuery = q;
        this.tagExpanded = {};
      } catch (e) {
        this.$message.error(this.$t('KnowledgeBasePage.tagsLoadError'));
        this.tagCategories = [];
        this.selectedTagIdsBySort = {};
        this.activeCollapseNames = [this.timePanelName];
      } finally {
        this.tagsLoading = false;
      }
    },
    // 构建标签查询
    buildTagSearch() {
      const result = [];
      this.tagCategories.forEach((cat) => {
        const ids = this.selectedTagIdsBySort[cat.tag_sort];
        if (ids && ids.length) {
          result.push(ids.map(String));
        }
      });
      return result;
    },
    // 构建列表参数
    buildListParams() {
      const params = {
        page_no: this.pageNo,
        page_size: this.pageSize,
      };
      const key = this.searchKey.trim();
      if (key) params.search_key = key;
      if (this.startYear) {
        const y = parseInt(this.startYear, 10);
        if (!Number.isNaN(y)) params.start_year = y;
      }
      if (this.endYear) {
        const y = parseInt(this.endYear, 10);
        if (!Number.isNaN(y)) params.end_year = y;
      }
      const tagSearch = this.buildTagSearch();
      if (tagSearch.length) params.tag_search = tagSearch;
      return params;
    },
    // 标签选择变化
    onTagSelectionChange() {
      this.fetchList(true);
    },
    // 每页条数变化
    onPageSizeChange(size) {
      this.pageSize = size;
      this.fetchList(true);
    },
    // 页码变化
    onPageChange(page) {
      this.pageNo = page;
      this.fetchList(false);
    },
    // 获取列表数据
    async fetchList(resetPage) {
      if (resetPage) this.pageNo = 1;
      this.listLoading = true;
      try {
        const params = this.buildListParams();
        const res = await getKnowledgeBaseList(params, {
          paramsSerializer: listParamsSerializer,
        });
        const payload = res.data.data || {};
        this.items = payload.items || [];
        this.total = payload.total != null ? payload.total : 0;
        if (payload.page_size != null) this.pageSize = payload.page_size;
        if (payload.page_no != null) this.pageNo = payload.page_no;
      } catch (e) {
        this.items = [];
        this.total = 0;
        this.$message.error(this.$t('KnowledgeBasePage.listLoadError'));
      } finally {
        this.listLoading = false;
      }
    },
    // 格式化发布年份
    formatYear(y) {
      if (y == null || y === '') return '发布年份：未知';
      return String('发布年份：' + y);
    },
    // 根据标题后缀名获取文件图标
    getFileIcon(fileName) {
      if (!fileName) {
        return require('@/assets/images/chatpreview/file.svg');
      }
      const extension = fileName.split('.').pop()?.toLowerCase() || '';
      switch (extension) {
        case 'doc':
        case 'docx':
          return require('@/assets/images/chatpreview/file_doc.svg');
        case 'xls':
        case 'xlsx':
          return require('@/assets/images/chatpreview/file_excel.svg');
        case 'jpg':
        case 'jpeg':
        case 'png':
        case 'gif':
        case 'bmp':
        case 'webp':
        case 'svg':
        case 'ico':
          return require('@/assets/images/chatpreview/file_img.svg');
        case 'pdf':
          return require('@/assets/images/chatpreview/file_pdf.svg');
        case 'ppt':
        case 'pptx':
          return require('@/assets/images/chatpreview/file_ppt.svg');
        default:
          return require('@/assets/images/chatpreview/file.svg');
      }
    },
    // 是否为 PDF 文件
    isPdfFile(fileName) {
      if (!fileName) return false;
      const extension = fileName.split('.').pop()?.toLowerCase() || '';
      return extension === 'pdf';
    },
    // 预览 PDF 文件
    async handlePreview(item) {
      if (!item.id) {
        this.$message.warning(this.$t('KnowledgeBasePage.downloadNoFile'));
        return;
      }
      this.revokePreviewUrl();
      this.previewTitle = item.title || this.$t('KnowledgeBasePage.preview');
      this.previewVisible = true;
      this.previewLoading = true;
      try {
        const res = await getKnowledgeFilePreview(item.id);
        // 如果响应头为application/pdf，则创建对象URL
        if (res.headers['content-type'] === 'application/pdf') {
          const blob = new Blob([res.data], { type: 'application/pdf' });
          this.previewUrl = URL.createObjectURL(blob);
        }else {
          this.previewVisible = false;
          this.$message.error(this.$t('KnowledgeBasePage.previewFail'));
        }
      } catch (e) {
        this.previewVisible = false;
        this.$message.error(this.$t('KnowledgeBasePage.previewFail'));
      } finally {
        this.previewLoading = false;
      }
    },
    revokePreviewUrl() {
      if (this.previewUrl) {
        URL.revokeObjectURL(this.previewUrl); // 释放对象URL
        this.previewUrl = '';
      }
    },
    closePreviewDialog() {
      this.revokePreviewUrl();
      this.previewTitle = '';
      this.previewLoading = false;
    },
    // 下载文件
    handleDownload(item) {
      if (!item.id) {
        this.$message.warning(this.$t('KnowledgeBasePage.downloadNoFile'));
        return;
      }
      getMinioFileUrl(item.id)
        .then((response) => {
          if (response.data.code === 400) {
            this.$message.error(response.data.data);
            return;
          }
          const url = response.data.data;
          const a = document.createElement('a');
          a.href = url;
          a.download = item.title || 'file';
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          this.$message.success(this.$t('KnowledgeBasePage.downloadSuccess'));
        })
        .catch(() => {
          this.$message.error(this.$t('KnowledgeBasePage.downloadFail'));
        });
    },
  },
};

/** 将 tag_search 作为单个 query 字段序列化为 JSON 二维数组，避免 axios 默认打成 tag_search[0][0] */
function listParamsSerializer(params) {
  const sp = new URLSearchParams();
  sp.set('page_no', String(params.page_no));
  sp.set('page_size', String(params.page_size));
  if (params.search_key != null && String(params.search_key).trim() !== '') {
    sp.set('search_key', String(params.search_key).trim());
  }
  if (params.start_year != null && params.start_year !== '') {
    sp.set('start_year', String(params.start_year));
  }
  if (params.end_year != null && params.end_year !== '') {
    sp.set('end_year', String(params.end_year));
  }
  if (params.tag_search != null && Array.isArray(params.tag_search) && params.tag_search.length > 0) {
    sp.set('tag_search', JSON.stringify(params.tag_search));
  }
  return sp.toString();
}
</script>

<style scoped lang="scss">
$kb-blue: #18a1f6;
$kb-text: #1a1a1a;

.knowledge-base-page {
  min-height: 100vh;
  background: #f0f2f5;
  display: flex;
  flex-direction: column;
}

.kb-header {
  background: #fff;
  border-bottom: 1px solid #e8e8e8;
  flex-shrink: 0;
}

.kb-header-inner {
  display: grid;
  grid-template-columns: minmax(120px, auto) 1fr minmax(100px, auto);
  align-items: center;
  gap: 16px;
  margin: 0 auto;
  padding: 16px 32px;
  box-sizing: border-box;
}

.kb-title {
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  color: $kb-text;
  white-space: nowrap;
}

.kb-search-wrap {
  display: flex;
  justify-content: center;
  min-width: 0;
}

.kb-search-input {
  width: 100%;
  max-width: 640px;
}

.kb-search-input ::v-deep .el-input__inner {
  border-radius: 4px 0 0 4px;
}

.kb-search-input ::v-deep .el-input-group__append {
  padding: 0;
  border: none;
  background: $kb-blue;
}

.kb-search-input ::v-deep .el-input-group__append .el-button {
  margin: -1px -1px -1px 0;
  border-radius: 0 4px 4px 0;
  border: none;
  background: $kb-blue;
  color: #fff;

  &:hover,
  &:focus {
    background: #3db0f8;
    color: #fff;
  }
}

.kb-back {
  font-size: 14px;
  color: $kb-text;
  justify-self: end;
  padding: 0;

  &:hover {
    color: $kb-blue;
  }

  .el-icon-back {
    margin-right: 4px;
  }
}

.kb-body {
  flex: 1;
  display: flex;
  margin: 0 auto;
  width: 100%;
  padding: 20px 32px 28px;
  box-sizing: border-box;
  gap: 20px;
  align-items: stretch;
}

.kb-sidebar {
  width: 300px;
  min-width: 280px;
  flex-shrink: 0;
  background: #f7f8fa;
  border-radius: 8px;
  border: 1px solid #e8eaed;
  padding: 16px 14px 18px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
  max-height: calc(100vh - 120px);
  overflow-y: auto;
}

.kb-sidebar-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
  padding: 0 2px;
}

.kb-sidebar-head-title {
  font-size: 16px;
  font-weight: 600;
  color: #303133;
}

.kb-link-reset,
.kb-link-clear {
  background: none;
  border: none;
  padding: 0;
  font-size: 14px;
  color: $kb-blue;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
}

.kb-tags-loading {
  text-align: center;
  padding: 32px;
  color: #909399;

  .el-icon-loading {
    font-size: 24px;
  }
}

.kb-filter-collapse {
  border: none;

  &::v-deep .el-collapse-item__header {
    font-size: 14px;
    font-weight: 600;
    color: #303133;
    border-bottom-color: #ebeef5;
    min-height: 44px;
    line-height: 44px;
    padding: 0 6px;
    margin-bottom: 10px;
  }

  &::v-deep .el-collapse-item__wrap {
    border-bottom: none;
    background: transparent;
  }

  &::v-deep .el-collapse-item__content {
  }
}

.kb-year-fields {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.kb-year-row {
  display: flex;
  align-items: center;
  gap: 10px;
}

.kb-year-label {
  flex: 0 0 72px;
  font-size: 13px;
  color: #606266;
}

.kb-year-picker {
  flex: 1;
  min-width: 0;
}

.kb-inline-search {
  margin-bottom: 10px;
}

.kb-checkbox-group {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.kb-checkbox {
  margin: 0;
  display: flex;
  align-items: center;
  white-space: normal;

  ::v-deep .el-checkbox__label {
    line-height: 1.5;
  }
}

.kb-checkbox-label {
  word-break: break-all;
}

.kb-tag-count {
  color: #909399;
  font-size: 12px;
  margin-left: 2px;
}

.kb-expand-link {
  display: block;
  margin-top: 8px;
  background: none;
  border: none;
  padding: 0;
  font-size: 13px;
  color: $kb-blue;
  cursor: pointer;
  text-align: left;

  &:hover {
    text-decoration: underline;
  }
}

.kb-selected-panel {
  margin-top: 16px;
  padding-top: 14px;
  border-top: 1px solid #ebeef5;
}

.kb-selected-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
  font-size: 14px;
  font-weight: 600;
  color: #303133;
}

.kb-chip-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.kb-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  font-size: 12px;
  color: #606266;
  background: #fff;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  max-width: 100%;
}

.kb-chip-x {
  cursor: pointer;
  color: #c0c4cc;
  font-size: 12px;

  &:hover {
    color: #f56c6c;
  }
}

.kb-main {
  flex: 1;
  min-width: 0;
  background: #fff;
  border-radius: 8px;
  border: 1px solid #e8eaed;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
}

.kb-main-inner {
  padding: 16px 20px 20px;
  min-height: 280px;
}

.kb-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 16px;
}

.kb-result-count {
  font-size: 14px;
  color: #606266;
}

.kb-view-toggle ::v-deep .el-radio-button__inner {
  padding: 8px 14px;
}

.kb-view-toggle ::v-deep .el-radio-button__orig-radio:checked + .el-radio-button__inner {
  border-color: $kb-blue;
  box-shadow: -1px 0 0 0 $kb-blue;
}

.kb-empty {
  text-align: center;
  color: #909399;
  padding: 48px 16px 24px;
  font-size: 14px;
}

.kb-card-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
}

.kb-card {
  position: relative;
  border: 1px solid #ebeef5;
  border-radius: 8px;
  padding: 16px 16px 12px;
  background: #fff;
  display: flex;
  flex-direction: column;
  transition: box-shadow 0.2s ease, border-color 0.2s ease;
  overflow: hidden;

  &:hover {
    border-color: #dcdfe6;
    box-shadow: 0 4px 14px rgba(0, 0, 0, 0.08);
  }
}

.kb-card-head {
  display: flex;
  gap: 12px;
  align-items: flex-start;
  padding-right: 36px;
}

.kb-card-icon {
  width: 50px;
  height: 50px;
  flex-shrink: 0;
  object-fit: contain;
}

.kb-card-head-main {
  min-width: 0;
  flex: 1;
}

.kb-card-title {
  margin: 0 0 8px;
  font-size: 16px;
  font-weight: 600;
  color: #303133;
  line-height: 1.35;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  overflow: hidden;
}

.kb-card-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 8px;
}

.kb-tag-pill {
  font-size: 12px;
  padding: 2px 8px;
  border-radius: 4px;
  background: #ecf5ff;
  color: #409eff;
  border: 1px solid #d9ecff;
}

.kb-tag-pill--sm {
  padding: 1px 6px;
  font-size: 11px;
}

.kb-card-meta-line,
.kb-list-meta {
  font-size: 13px;
  color: #909399;
  margin-bottom: 8px;
}

.kb-meta-sep {
  margin: 0 6px;
  color: #dcdfe6;
}

.kb-card-summary {
  margin: 0 0 10px;
  font-size: 13px;
  color: #606266;
  line-height: 1.55;
  flex: 1;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 3;
  line-clamp: 3;
  overflow: hidden;
}

.kb-card-footer {
  margin-top: auto;
  text-align: right;
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}

.kb-list-actions {
  display: flex;
  align-items: flex-end;
  gap: 4px;
  flex-shrink: 0;
}

.kb-download-link {
  color: $kb-blue;
  font-size: 13px;
  padding: 0;

  &:hover {
    color: #0d8bdc;
  }
}

.kb-list {
  border: 1px solid #ebeef5;
  border-radius: 8px;
  overflow: hidden;
}

.kb-list-row {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  padding: 14px 16px;
  border-bottom: 1px solid #f0f2f5;
  background: #fff;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: #fafbfc;
  }
}

.kb-list-icon {
  width: 36px;
  height: 36px;
  flex-shrink: 0;
  object-fit: contain;
  margin-top: 2px;
}

.kb-list-main {
  flex: 1;
  min-width: 0;
}

.kb-list-title-row {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 8px;
  margin-bottom: 6px;
}

.kb-list-title {
  font-size: 15px;
  font-weight: 600;
  color: #303133;
}

.kb-list-tags {
  display: inline-flex;
  flex-wrap: wrap;
  gap: 4px;
}

.kb-list-summary {
  margin: 6px 0 0;
  font-size: 13px;
  color: #606266;
  line-height: 1.5;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  overflow: hidden;
}

.kb-pagination-wrap {
  margin-top: 20px;
  display: flex;
  justify-content: flex-end;
  flex-wrap: wrap;

  ::v-deep .el-pagination__jump {
    margin-left: 8px;
  }
}

.kb-preview-dialog ::v-deep .el-dialog__body {
  padding: 0 20px 20px;
}

.kb-preview-body {
  min-height: 70vh;
  height: 70vh;
}

.kb-preview-iframe {
  width: 100%;
  height: 100%;
  border: none;
  background: #f5f7fa;
}
</style>
